import { useTranslate } from '@refinedev/core';
import {
  Card,
  Col,
  Descriptions,
  Flex,
  Grid,
  Row,
  Space,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import type { DescriptionsProps } from 'antd';
import type {
  PublicationListOut,
  SessionDetailOut,
} from '@/api/generated/schemas';
import { orDash, useVerticalDescProps } from '@/components/common/show/shared';
import { CoverageBar } from '@/components/common/CoverageBar';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import { ProfileIconLink } from '@/components/account/ProfileIconLink';
import { PublicationInfoPopover } from '@/components/user_catalog/publications/PublicationInfoPopover';
import {
  ProductFormatTag,
  ProductVariantTag,
} from '@/components/progress/tags';
import { formatDateTime, formatSecondsClock } from '@/utils/format';
import { SessionHourlyCard } from './SessionHourlyCard';
import { PublicationDrawerIcon } from '@/components/user_catalog/publications/drawer/PublicationDrawerIcon';

type Props = {
  record: SessionDetailOut;
  publication?: PublicationListOut;
};

/** Seconds rendered as a clock, with the raw count one hover away. */
const SecondsCell = ({ value }: { value?: number | null }) =>
  orDash(
    value != null && (
      <Tooltip title={`${value} s`}>
        <span>{formatSecondsClock(value, { alwaysHours: true })}</span>
      </Tooltip>
    )
  );

export const SessionGeneralTab = ({ record, publication }: Props) => {
  const t = useTranslate();
  const verticalDescProps = useVerticalDescProps();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();

  const entitlement = record.entitlement;
  const duration = record.product_duration;
  const ranges = record.progression_ranges ?? [];
  const coveragePct =
    duration > 0 ? Math.round((record.covered_seconds / duration) * 100) : 0;

  const basicItems: DescriptionsProps['items'] = [
    { key: 'id', label: t('base.fields.id'), children: orDash(record.id) },
    {
      key: 'account_id',
      label: t('progress.sessions.fields.account_id'),
      children: orDash(record.account_id),
    },
    {
      key: 'profile_id',
      label: t('progress.sessions.fields.profile_id'),
      children: orDash(
        record.profile_id != null && (
          <ProfileIconLink profileId={record.profile_id} />
        )
      ),
    },
    {
      key: 'publisher_id',
      label: t('progress.sessions.fields.publisher_id'),
      children: orDash(record.publisher_id),
    },
    {
      key: 'publication_id',
      label: t('progress.sessions.fields.publication_id'),
      children: orDash(
        record.publication_id != null && (
          <PublicationInfoPopover
            publication={publication}
            publicationId={record.publication_id}
          />
        )
      ),
    },
    {
      key: 'entitlement_id',
      label: t('progress.sessions.fields.entitlement_id'),
      children: orDash(
        record.entitlement_id != null && (
          <ShowIconLink
            resource="monetization.entitlements"
            id={record.entitlement_id}
          />
        )
      ),
    },
  ];

  const productItems: DescriptionsProps['items'] = [
    {
      key: 'product_id',
      label: t('progress.sessions.fields.product_id'),
      children: orDash(
        record.product_id != null && (
          <Space size={4}>
            {record.product_id}
            {record.publication_id != null && (
              <PublicationDrawerIcon publicationId={record.publication_id} />
            )}
          </Space>
        )
      ),
    },
    {
      key: 'product_version',
      label: t('progress.sessions.fields.product_version'),
      children: orDash(record.product_version),
    },
    {
      key: 'product_kind',
      label: t('progress.sessions.fields.product_kind'),
      children: (
        <ProductFormatTag value={record.product_kind} labels="sessions" />
      ),
    },
    {
      key: 'product_variant',
      label: t('progress.sessions.fields.product_variant'),
      children: (
        <ProductVariantTag value={record.product_variant} labels="sessions" />
      ),
    },
    {
      key: 'product_duration',
      label: t('progress.sessions.fields.product_duration'),
      children: <SecondsCell value={duration} />,
    },
    {
      key: 'product_demo_ratio',
      label: t('progress.sessions.fields.product_demo_ratio'),
      children: orDash(record.product_demo_ratio),
    },
  ];

  const entitlementItems: DescriptionsProps['items'] = entitlement
    ? [
        {
          key: 'id',
          label: t('base.fields.id'),
          children: (
            <ShowIconLink
              resource="monetization.entitlements"
              id={entitlement.id}
            />
          ),
        },
        {
          key: 'account_id',
          label: t('progress.sessions.fields.account_id'),
          children: orDash(entitlement.account_id),
        },
        {
          key: 'valid_from',
          label: t('progress.sessions.fields.valid_from'),
          children: orDash(formatDateTime(entitlement.valid_from)),
        },
        {
          key: 'valid_until',
          label: t('progress.sessions.fields.valid_until'),
          children: orDash(formatDateTime(entitlement.valid_until)),
        },
        {
          key: 'limit_seconds',
          label: t('progress.sessions.fields.limit_seconds'),
          children: <SecondsCell value={entitlement.limit_seconds} />,
        },
        {
          key: 'consumed_seconds',
          label: t('progress.sessions.fields.consumed_seconds'),
          children: <SecondsCell value={entitlement.consumed_seconds} />,
        },
        {
          key: 'limit_reached_at',
          label: t('progress.sessions.fields.limit_reached_at'),
          children: orDash(formatDateTime(entitlement.limit_reached_at)),
        },
      ]
    : [];

  const timeItems: DescriptionsProps['items'] = [
    {
      key: 'created_at',
      label: t('progress.sessions.fields.created_at'),
      children: orDash(formatDateTime(record.created_at)),
    },
    {
      key: 'updated_at',
      label: t('progress.sessions.fields.updated_at'),
      children: orDash(formatDateTime(record.updated_at)),
    },
  ];

  return (
    <Row gutter={[token.margin, token.margin]}>
      <Col flex="1 1 480px">
        <Flex vertical gap={token.margin}>
          <Card title={t('base.sections.basic_info')}>
            <Descriptions
              {...verticalDescProps}
              column={{ xs: 1, sm: 2 }}
              items={basicItems}
            />
          </Card>
          <Card title={t('progress.sessions.sections.product')}>
            <Descriptions
              {...verticalDescProps}
              column={{ xs: 1, sm: 2 }}
              items={productItems}
            />
          </Card>
          <Card title={t('progress.sessions.sections.coverage')}>
            <Flex vertical gap={token.margin}>
              <Flex gap={token.marginLG} align="center" wrap>
                <Descriptions
                  {...verticalDescProps}
                  column={{ xs: 1, sm: 2 }}
                  items={[
                    {
                      key: 'covered_seconds',
                      label: t('progress.sessions.fields.covered_seconds'),
                      children: <SecondsCell value={record.covered_seconds} />,
                    },
                    {
                      key: 'coverage_pct',
                      label: t('progress.sessions.fields.coverage_pct'),
                      // Meaningless without a duration to divide by.
                      children: orDash(duration > 0 && `${coveragePct}%`),
                    },
                  ]}
                />
              </Flex>

              <div>
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: token.fontSizeSM }}
                >
                  {t('progress.sessions.fields.progression_ranges')}
                </Typography.Text>
                {/* Ranges are fractions of the product, so they map straight
                    onto a bar whose full width is product_duration. */}
                {ranges.length ? (
                  <CoverageBar
                    ranges={ranges}
                    totalSeconds={duration}
                    hint={t('progress.sessions.hints.progression_ranges')}
                  />
                ) : (
                  <div>{orDash(null)}</div>
                )}
              </div>
            </Flex>
          </Card>

          <Card title={t('base.sections.system_info')}>
            <Descriptions
              {...verticalDescProps}
              column={{ xs: 1, sm: 2 }}
              items={timeItems}
            />
          </Card>
        </Flex>
      </Col>
      <Col flex={screens.lg ? '0 1 440px' : '0 1 300px'}>
        <Flex vertical gap={token.margin}>
          <Card title={t('progress.sessions.sections.entitlement')}>
            {entitlement ? (
              <Descriptions
                {...verticalDescProps}
                column={{ xs: 1, sm: 2 }}
                items={entitlementItems}
              />
            ) : (
              <Tag color="gold">{t('progress.sessions.entitlement.none')}</Tag>
            )}
          </Card>

          <SessionHourlyCard sessionId={record.id} />
        </Flex>
      </Col>
    </Row>
  );
};
